export type UrlFieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'stringArray'
  | 'numberArray'
  | 'enum';

export interface UrlField<T> {
  type: UrlFieldType;
  default: T;
  param?: string;
  parse: (raw: string | null) => T;
  serialize: (value: T) => string;
}

export type UrlSchema = Record<string, UrlField<any>>;

export type UrlStateOf<S extends UrlSchema> = {
  [K in keyof S]: S[K]['default'];
};

export const URL_ARRAY_LIMIT = 50;

const splitList = (raw: string) =>
  raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

const uniqueCapped = <T>(items: T[]) =>
  [...new Set(items)].slice(0, URL_ARRAY_LIMIT);

interface NumberOptions {
  integer?: boolean;
  min?: number;
  max?: number;
}

export const urlField = {
  string: (def = '', param?: string): UrlField<string> => ({
    type: 'string',
    default: def,
    param,
    parse: (raw) => raw ?? def,
    serialize: (value) => value,
  }),

  number: <D extends number | null>(
    def: D,
    { integer = false, min, max }: NumberOptions = {},
    param?: string,
  ): UrlField<number | D> => ({
    type: 'number',
    default: def,
    param,
    parse: (raw) => {
      if (raw === null || raw.trim() === '') return def;
      const n = Number(raw);
      if (!Number.isFinite(n)) return def;
      if (integer && !Number.isInteger(n)) return def;
      if (min !== undefined && n < min) return def;
      if (max !== undefined && n > max) return def;
      return n;
    },
    serialize: (value) => (value === null ? '' : String(value)),
  }),

  page: (def = 1, param?: string) =>
    urlField.number(def, { integer: true, min: 1 }, param),

  boolean: (def = false, param?: string): UrlField<boolean> => ({
    type: 'boolean',
    default: def,
    param,
    parse: (raw) => {
      if (raw === '1' || raw === 'true') return true;
      if (raw === '0' || raw === 'false') return false;
      return def;
    },
    serialize: (value) => (value ? '1' : '0'),
  }),

  enum: <V extends string>(
    values: readonly V[],
    def: V,
    param?: string,
  ): UrlField<V> => ({
    type: 'enum',
    default: def,
    param,
    parse: (raw) =>
      raw !== null && (values as readonly string[]).includes(raw)
        ? (raw as V)
        : def,
    serialize: (value) => value,
  }),

  stringArray: <V extends string = string>(
    values?: readonly V[],
    param?: string,
  ): UrlField<V[]> => ({
    type: 'stringArray',
    default: [],
    param,
    parse: (raw) => {
      if (!raw) return [];
      const items = splitList(raw).filter(
        (item) => !values || (values as readonly string[]).includes(item),
      );
      return uniqueCapped(items as V[]);
    },
    serialize: (value) => uniqueCapped(value).join(','),
  }),

  numberArray: (param?: string): UrlField<number[]> => ({
    type: 'numberArray',
    default: [],
    param,
    parse: (raw) => {
      if (!raw) return [];
      const ids = splitList(raw)
        .map(Number)
        .filter((n) => Number.isInteger(n) && n > 0);
      return uniqueCapped(ids);
    },
    serialize: (value) => uniqueCapped(value).join(','),
  }),
};

const paramOf = (schema: UrlSchema, key: string) => schema[key].param ?? key;

export const isDefaultValue = <T>(field: UrlField<T>, value: T) =>
  field.serialize(value) === field.serialize(field.default);

export function defaultUrlState<S extends UrlSchema>(schema: S): UrlStateOf<S> {
  const state = {} as UrlStateOf<S>;
  for (const key of Object.keys(schema) as (keyof S & string)[]) {
    state[key] = schema[key].default;
  }
  return state;
}

export function parseUrlState<S extends UrlSchema>(
  schema: S,
  search: string,
): UrlStateOf<S> {
  const params = new URLSearchParams(search);
  const state = {} as UrlStateOf<S>;
  for (const key of Object.keys(schema) as (keyof S & string)[]) {
    state[key] = schema[key].parse(params.get(paramOf(schema, key)));
  }
  return state;
}

export function serializeUrlState<S extends UrlSchema>(
  schema: S,
  state: UrlStateOf<S>,
  search = '',
): string {
  const params = new URLSearchParams(search);
  for (const key of Object.keys(schema) as (keyof S & string)[]) {
    const field = schema[key];
    const param = paramOf(schema, key);
    params.delete(param);
    if (!isDefaultValue(field, state[key])) {
      params.set(param, field.serialize(state[key]));
    }
  }
  return params.toString().replace(/%2C/gi, ',');
}

export function mergeUrlState<S extends UrlSchema>(
  schema: S,
  current: UrlStateOf<S>,
  patch: Partial<UrlStateOf<S>>,
  pageKey?: keyof S & string,
  resetPage = true,
): UrlStateOf<S> {
  const next = { ...current, ...patch };
  if (!pageKey || pageKey in patch || !resetPage) return next;
  const changed = (Object.keys(patch) as (keyof S & string)[]).some(
    (key) =>
      key !== pageKey &&
      schema[key] &&
      schema[key].serialize(patch[key]) !== schema[key].serialize(current[key]),
  );
  if (changed) next[pageKey] = schema[pageKey].default;
  return next;
}

export const compact = <T extends object>(value: T): T =>
  Object.fromEntries(
    Object.entries(value).filter(([, v]) => v !== undefined),
  ) as T;
